import React, { useEffect, useState } from "react";
import { getOrganizationPosts, getUserPosts } from "../../api/api";
import { Skeleton } from "../ui/skeleton";
import PostDetailModal from "./PostDetailModal";
import PostPreviewCard from "./PostPreviewCard";

/**
 * Fetch and display an author's posts as a preview grid with a detail modal.
 * @param {Object} props
 * @param {number} props.viewerId - Logged-in viewer id
 * @param {object} props.viewerUser - Logged-in viewer user object
 * @param {number} props.authorId - Profile user or organization id
 * @param {boolean} [props.isOrg]
 * @returns {JSX.Element}
 */
function ProfilePostsSection({ viewerId, viewerUser, authorId, isOrg = false }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPostId, setSelectedPostId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!authorId) {
        setPosts([]);
        setError("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const result = isOrg
          ? await getOrganizationPosts(authorId)
          : await getUserPosts(authorId);
        if (cancelled) return;
        if (result?.error) {
          setError(result.error);
          setPosts([]);
        } else if (Array.isArray(result?.data)) {
          setPosts(result.data);
        } else {
          setPosts([]);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading profile posts:", err);
        setError("Failed to load posts");
        setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [authorId, isOrg]);

  const handleDeleted = (postId) => {
    setPosts((current) => current.filter((post) => post.id !== postId));
    setSelectedPostId(null);
  };

  return (
    <>
      {loading ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="aspect-square w-full rounded-md" />
          ))}
        </div>
      ) : null}

      {!loading && error ? (
        <p className="text-sm text-error">{error}</p>
      ) : null}

      {!loading && !error && posts.length === 0 ? (
        <p className="text-sm text-text-muted">No posts yet.</p>
      ) : null}

      {!loading && !error && posts.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {posts.map((post) => (
            <PostPreviewCard
              key={post.id}
              post={post}
              onSelect={setSelectedPostId}
            />
          ))}
        </div>
      ) : null}

      <PostDetailModal
        open={selectedPostId != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSelectedPostId(null);
        }}
        postId={selectedPostId}
        userId={viewerId}
        user={viewerUser}
        onDeleted={handleDeleted}
      />
    </>
  );
}

export default ProfilePostsSection;
